// Central application store (zustand). It owns the measurement engine host, the
// node list, the settings form, live run/sweep state, local history, and the
// theme. Pages and charts read from here; the engine host posts events that this
// store folds into state. The host runs on the main thread (RTCPeerConnection is
// not available in Web Workers).
//
// Constraint 1 (no parallel probing) is honoured structurally: a run submits an
// ordered list of nodes and the host probes them strictly one at a time; the
// store simply reflects whichever node is currently active.

import { create } from 'zustand';
import {
  TIER_ORDER,
  type CellResult,
  type LadderRung,
  type NodeInfo,
  type RunResult,
  type Settings,
  type SweepResult,
  type SweepSpec,
  type Verdict,
} from '@/engine/types';
import {
  createEngineHost,
  type EngineHost,
  type WorkerCommand,
  type WorkerEvent,
} from '@/engine/host';
import { buildLadder } from '@/engine/metrics';
import { detectTheme, type ThemeName } from '@/charts';
import * as api from '@/lib/api';
import {
  listHistory,
  newHistoryId,
  saveHistory,
  markShared,
  deleteHistory,
  type HistoryEntry,
} from '@/lib/history';
import {
  makeRunPayload,
  makeSweepPayload,
  type SharePayload,
} from '@/lib/share';

/** Overall lifecycle of a single-run measurement. */
export type RunState = 'idle' | 'connecting' | 'running' | 'done';
/** Overall lifecycle of a sweep. */
export type SweepPhase = 'idle' | 'connecting' | 'running' | 'done';

/** Live counters for one node during a run. */
export interface NodeProgress {
  sent: number;
  echoed: number;
  total: number;
  elapsedMs: number;
}

/** Live sweep position. */
export interface SweepProgress {
  index: number;
  total: number;
  nodeId: string;
  size: number;
  pps: number;
  repeat: number;
}

const THEME_KEY = 'plm-theme';
const WARMUP_DEFAULT = 1000;

/** Default settings (mirrors the "default" preset, parity with the reference). */
export const DEFAULT_SETTINGS: Settings = {
  sizes: [212, 228],
  pps: 15,
  durationMs: 10000,
  // Tuned for low-latency in-network testing (RTT ~10-20 ms): a 50 ms threshold
  // flags genuinely late packets without the 200 ms reference value hiding them.
  acceptableDelayMs: 50,
  ipMode: 'auto',
  // Warm-up is always applied (it lets DTLS/SCTP settle before recording) and is
  // no longer user-adjustable.
  warmupMs: WARMUP_DEFAULT,
};

export interface StoreState {
  // --- nodes / bootstrap ---
  nodes: NodeInfo[];
  nodesLoading: boolean;
  nodesError: string | null;
  selectedNodeIds: string[];

  // --- settings ---
  settings: Settings;

  // --- single run ---
  runState: RunState;
  activeNodeId: string | null;
  progress: Record<string, NodeProgress>;
  results: Record<string, RunResult>;
  runOrder: string[];
  runNodes: NodeInfo[];
  verdict: Verdict | null;
  ladder: LadderRung[];

  // --- sweep ---
  sweepPhase: SweepPhase;
  sweepSpec: SweepSpec | null;
  sweepNodes: NodeInfo[];
  sweepProgress: SweepProgress | null;
  sweepCells: CellResult[];
  sweepResult: SweepResult | null;

  // --- misc ---
  engineError: string | null;
  history: HistoryEntry[];
  theme: ThemeName;
  // History id of the current run/sweep once persisted, so repeated "Save to
  // history" / "Share" reuse one entry instead of piling up duplicates. Cleared
  // when a new run/sweep starts.
  runSavedId: string | null;
  sweepSavedId: string | null;

  // --- actions ---
  init: () => Promise<void>;
  refreshNodes: () => Promise<void>;
  setSelectedNodes: (ids: string[]) => void;
  toggleNode: (id: string) => void;
  setSettings: (patch: Partial<Settings>) => void;
  replaceSettings: (settings: Settings) => void;
  /** Reverse route trace opt-in (consent); not part of shareable settings. */
  traceroute: boolean;
  setTraceroute: (v: boolean) => void;
  setTheme: (theme: ThemeName) => void;

  startRun: () => Promise<void>;
  stopRun: () => void;
  resetRun: () => void;

  startSweep: (spec: SweepSpec) => Promise<void>;
  stopSweep: () => void;
  resetSweep: () => void;

  persistRun: (share: boolean) => Promise<string | null>;
  persistSweep: (share: boolean) => Promise<string | null>;

  refreshHistory: () => Promise<void>;
  removeHistory: (id: string) => Promise<void>;

  applyWorkerEvent: (event: WorkerEvent) => void;
}

// The engine host is a process-level singleton, not part of serialisable state.
// It runs on the main thread because RTCPeerConnection is a Window-only API and
// is not available in Web Workers.
let host: EngineHost | null = null;

function ensureWorker(apply: (ev: WorkerEvent) => void): EngineHost | null {
  if (host) return host;
  if (typeof RTCPeerConnection === 'undefined') return null;
  host = createEngineHost(apply);
  return host;
}

function postCommand(cmd: WorkerCommand): void {
  host?.handle(cmd);
}

/** Order selected node ids outward by tier, then by name, for sequential runs. */
function orderNodes(nodes: NodeInfo[], selected: string[]): NodeInfo[] {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const picked = selected
    .map((id) => byId.get(id))
    .filter((n): n is NodeInfo => n !== undefined);
  return picked.sort((a, b) => {
    const ta = TIER_ORDER.indexOf(a.tier);
    const tb = TIER_ORDER.indexOf(b.tier);
    if (ta !== tb) return ta - tb;
    return a.name.localeCompare(b.name);
  });
}

/** Read the persisted theme, falling back to the detected one. */
function initialTheme(): ThemeName {
  try {
    const stored = localStorage.getItem(THEME_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'high-contrast') {
      return stored;
    }
  } catch {
    // localStorage may be unavailable; fall through.
  }
  return detectTheme();
}

/** Apply a theme to the document root and persist it. */
function applyTheme(theme: ThemeName): void {
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('data-theme', theme);
  }
  try {
    localStorage.setItem(THEME_KEY, theme);
  } catch {
    // ignore
  }
}

export const useStore = create<StoreState>((set, get) => ({
  nodes: [],
  nodesLoading: false,
  nodesError: null,
  selectedNodeIds: [],

  settings: DEFAULT_SETTINGS,
  traceroute: false,

  runState: 'idle',
  activeNodeId: null,
  progress: {},
  results: {},
  runOrder: [],
  runNodes: [],
  verdict: null,
  ladder: [],

  sweepPhase: 'idle',
  sweepSpec: null,
  sweepNodes: [],
  sweepProgress: null,
  sweepCells: [],
  sweepResult: null,

  engineError: null,
  history: [],
  theme: initialTheme(),
  runSavedId: null,
  sweepSavedId: null,

  async init() {
    const theme = get().theme;
    applyTheme(theme);
    ensureWorker(get().applyWorkerEvent);
    await Promise.all([get().refreshNodes(), get().refreshHistory()]);
  },

  async refreshNodes() {
    set({ nodesLoading: true, nodesError: null });
    try {
      const nodes = await api.fetchNodes();
      set((s) => ({
        nodes,
        nodesLoading: false,
        // Drop selections that no longer exist.
        selectedNodeIds: s.selectedNodeIds.filter((id) => nodes.some((n) => n.id === id)),
      }));
    } catch (err) {
      set({ nodesLoading: false, nodesError: err instanceof Error ? err.message : String(err) });
    }
  },

  setSelectedNodes(ids) {
    set({ selectedNodeIds: [...new Set(ids)] });
  },

  toggleNode(id) {
    set((s) => ({
      selectedNodeIds: s.selectedNodeIds.includes(id)
        ? s.selectedNodeIds.filter((x) => x !== id)
        : [...s.selectedNodeIds, id],
    }));
  },

  setSettings(patch) {
    set((s) => ({ settings: { ...s.settings, ...patch } }));
  },

  setTraceroute(v) {
    set({ traceroute: v });
  },

  replaceSettings(settings) {
    set({ settings });
  },

  setTheme(theme) {
    applyTheme(theme);
    set({ theme });
  },

  async startRun() {
    const state = get();
    if (state.runState === 'running' || state.runState === 'connecting') return;
    const ordered = orderNodes(state.nodes, state.selectedNodeIds);
    if (ordered.length === 0) return;

    set({
      runState: 'connecting',
      activeNodeId: null,
      progress: {},
      results: {},
      runOrder: ordered.map((n) => n.id),
      runNodes: ordered,
      verdict: null,
      ladder: [],
      engineError: null,
      runSavedId: null,
    });

    const w = ensureWorker(get().applyWorkerEvent);
    if (!w) {
      set({ runState: 'idle', engineError: 'WebRTC (RTCPeerConnection) is unavailable. Use a modern browser, over HTTPS or on localhost.' });
      return;
    }
    try {
      const boot = await api.bootstrapSignaling();
      postCommand({
        type: 'run',
        config: { url: boot.url, token: boot.token },
        nodes: ordered,
        settings: state.settings,
        iceServers: boot.iceServers,
        sessionInfo: boot.sessionInfo,
        traceroute: state.traceroute,
      });
    } catch (err) {
      set({
        runState: 'idle',
        engineError: err instanceof Error ? err.message : String(err),
      });
    }
  },

  stopRun() {
    postCommand({ type: 'cancel' });
  },

  resetRun() {
    set({
      runState: 'idle',
      activeNodeId: null,
      progress: {},
      results: {},
      runOrder: [],
      runNodes: [],
      verdict: null,
      ladder: [],
      engineError: null,
      runSavedId: null,
    });
  },

  async startSweep(spec) {
    const state = get();
    if (state.sweepPhase === 'running' || state.sweepPhase === 'connecting') return;
    if (spec.nodes.length === 0) return;
    const sweepNodes = state.nodes.filter((n) => spec.nodes.includes(n.id));

    set({
      sweepPhase: 'connecting',
      sweepSpec: spec,
      sweepNodes,
      sweepProgress: null,
      sweepCells: [],
      sweepResult: null,
      engineError: null,
      sweepSavedId: null,
    });

    const w = ensureWorker(get().applyWorkerEvent);
    if (!w) {
      set({ sweepPhase: 'idle', engineError: 'WebRTC (RTCPeerConnection) is unavailable. Use a modern browser, over HTTPS or on localhost.' });
      return;
    }
    try {
      const boot = await api.bootstrapSignaling();
      postCommand({
        type: 'sweep',
        config: { url: boot.url, token: boot.token },
        spec,
        nodeInfos: sweepNodes,
        iceServers: boot.iceServers,
        sessionInfo: boot.sessionInfo,
      });
    } catch (err) {
      set({
        sweepPhase: 'idle',
        engineError: err instanceof Error ? err.message : String(err),
      });
    }
  },

  stopSweep() {
    postCommand({ type: 'cancel' });
  },

  resetSweep() {
    set({
      sweepPhase: 'idle',
      sweepSpec: null,
      sweepNodes: [],
      sweepProgress: null,
      sweepCells: [],
      sweepResult: null,
      engineError: null,
      sweepSavedId: null,
    });
  },

  async persistRun(share) {
    const s = get();
    const runs = s.runOrder.map((id) => s.results[id]).filter((r): r is RunResult => !!r);
    if (runs.length === 0) return null;
    const payload = makeRunPayload(runs, s.runNodes, s.verdict);
    const { historyId, shareId } = await persistPayload(payload, share, get, s.runSavedId);
    set({ runSavedId: historyId });
    return shareId;
  },

  async persistSweep(share) {
    const s = get();
    if (!s.sweepResult) return null;
    const payload = makeSweepPayload(s.sweepResult, s.sweepNodes);
    const { historyId, shareId } = await persistPayload(payload, share, get, s.sweepSavedId);
    set({ sweepSavedId: historyId });
    return shareId;
  },

  async refreshHistory() {
    const history = await listHistory();
    set({ history });
  },

  async removeHistory(id) {
    await deleteHistory(id);
    await get().refreshHistory();
  },

  applyWorkerEvent(event) {
    switch (event.type) {
      case 'ready':
        return;

      case 'nodes':
        set({ nodes: event.nodes });
        return;

      case 'nodeStart':
        set((s) => ({
          runState: 'running',
          activeNodeId: event.nodeId,
          progress: {
            ...s.progress,
            [event.nodeId]: s.progress[event.nodeId] ?? { sent: 0, echoed: 0, total: 0, elapsedMs: 0 },
          },
        }));
        return;

      case 'progress':
        set((s) => ({
          progress: {
            ...s.progress,
            [event.nodeId]: {
              sent: event.sent,
              echoed: event.echoed,
              total: event.total,
              elapsedMs: event.elapsedMs,
            },
          },
        }));
        return;

      case 'runResult':
        set((s) => ({
          results: { ...s.results, [event.result.nodeId]: event.result },
        }));
        return;

      case 'runsDone':
        set({
          runState: 'done',
          activeNodeId: null,
          verdict: event.verdict,
          ladder: buildLadder(event.results),
        });
        return;

      case 'sweepCellStart':
        set({
          sweepPhase: 'running',
          sweepProgress: {
            index: event.index,
            total: event.total,
            nodeId: event.nodeId,
            size: event.size,
            pps: event.pps,
            repeat: event.repeat,
          },
        });
        return;

      case 'sweepCell':
        set((s) => ({ sweepCells: [...s.sweepCells, event.cell] }));
        return;

      case 'sweepDone':
        set({ sweepPhase: 'done', sweepProgress: null, sweepResult: event.result });
        return;

      case 'signalError':
        set({ engineError: event.error });
        return;

      case 'error':
        set((s) => ({
          engineError: event.message,
          runState: s.runState === 'connecting' || s.runState === 'running' ? 'idle' : s.runState,
          sweepPhase:
            s.sweepPhase === 'connecting' || s.sweepPhase === 'running' ? 'idle' : s.sweepPhase,
        }));
        return;

      case 'cancelled':
        set((s) => ({
          runState: s.runState === 'running' || s.runState === 'connecting'
            ? (Object.keys(s.results).length > 0 ? 'done' : 'idle')
            : s.runState,
          activeNodeId: null,
          sweepPhase: s.sweepPhase === 'running' || s.sweepPhase === 'connecting'
            ? (s.sweepCells.length > 0 ? 'done' : 'idle')
            : s.sweepPhase,
          sweepProgress: null,
        }));
        return;

      default:
        return;
    }
  },
}));

/** Save a payload to local history and, when sharing, upload it for a link. */
async function persistPayload(
  payload: SharePayload,
  share: boolean,
  get: () => StoreState,
  existingId: string | null,
): Promise<{ historyId: string; shareId: string | null }> {
  // Reuse the existing history id for this run/sweep so repeated saves/shares
  // upsert one entry (IndexedDB put keys on id) instead of appending duplicates.
  const id = existingId ?? newHistoryId();
  const entry: HistoryEntry = { id, createdAt: payload.createdAt, payload };
  await saveHistory(entry);
  await get().refreshHistory();
  if (!share) {
    return { historyId: id, shareId: null };
  }
  const shareId = await api.saveResult(payload);
  await markShared(id, shareId);
  await get().refreshHistory();
  return { historyId: id, shareId };
}
